import { computed, Ref, ref } from '@vue/composition-api';
import { i18n } from '@tt4b/ttam-i18n';
import { difference } from 'lodash-es';
import { useAllowlist, useCreativeActionTypeIs, useStdCreativeAdgroupIs } from '@tt4b/ttam-hooks';
import {
  AdgroupFormData,
  AdStatusValues,
  BrandSafetyTrackTypeMap,
  CampaignFormData,
  FullNormalFormData,
} from '@tt4b/ttam-enums';
import { useBrandSafetySupport } from './useBrandSafetySupport';
export function handleBoolBsSupportCountry(country: number[], supportCountryList: number[]) {
  const unsupportedCountries = difference(country, supportCountryList);
  return !unsupportedCountries.length;
}

export function useBrandSafetyProps(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData>;
  isDisabledCreative: Ref<boolean>;
}) {
  const { adgroupData, campaignData, isDisabledCreative } = options;
  const { isBsIasWhite, isZefrWhite, isBsDvWhite } = useAllowlist();

  const { isSupportBrandSafetyTracking } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isEdit } = useCreativeActionTypeIs({
    createMode: computed(() => ''),
    adgroupFormData: adgroupData,
  });

  const auditStatus = computed(() => (adgroupData.value as any).auditStatus);

  const {
    hasBsIasAuthority,
    hasZefrAuthority,
    hasBsDvAuthority,
    supportBsIasCountryList,
    supportBsZefrCountryList,
    supportBsDvCountryList,
  } = useBrandSafetySupport();

  /** 是否展示zefr */
  const isSupportedCountriesZefr = computed(() =>
    handleBoolBsSupportCountry(adgroupData.value?.country, supportBsZefrCountryList.value),
  );
  const isShowZefr = computed(() => isZefrWhite.value && isSupportedCountriesZefr.value);

  /** 是否展示BS IAS */
  const isSupportedCountriesIas = computed(() =>
    handleBoolBsSupportCountry(adgroupData.value?.country, supportBsIasCountryList.value),
  );
  const isShowBsIas = computed(() => isBsIasWhite.value && isSupportedCountriesIas.value);

  /** 是否展示Bs Dv */
  const isSupportedCountriesDv = computed(() =>
    handleBoolBsSupportCountry(adgroupData.value?.country, supportBsDvCountryList.value),
  );
  const isShowBsDv = computed(() => isBsDvWhite.value && isSupportedCountriesDv.value);

  /** 是否展示BS开关 */
  const isShowBrandSafety = computed(
    () => isSupportBrandSafetyTracking.value && (isShowZefr.value || isShowBsIas.value || isShowBsDv.value),
  );

  /** 判断创意是否通过审核 & 是否禁止编辑 */
  const isDisabledBrandSafetyTrack = computed(
    () => (isEdit.value && auditStatus.value !== AdStatusValues.AUDIT) || isDisabledCreative.value,
  );

  /** 所有可见行选项 */
  const allViewabilityOptions = computed(() => [
    {
      value: BrandSafetyTrackTypeMap.ZEFR,
      label: i18n.t('module_brand_safety_zefr'),
      hintKey: 'module_brand_safety_zefr_info_new',
      linkUrl: 'https://measurement.zefr.com/tiktok/connect',
      linkKey: 'module_brand_safety_zefr_info_link',
      hide: !isShowZefr.value,
      disabled: !hasZefrAuthority.value,
      isInAllowList: isZefrWhite.value,
    },
    {
      value: BrandSafetyTrackTypeMap.IAS,
      label: i18n.t('module_track_url_ias_checkbox_label'),
      hintKey: 'module_brand_safety_ias_info_new',
      linkUrl: 'https://reporting.integralplatform.com/campaign-setup',
      linkKey: 'module_brand_safety_ias_info_link',
      hide: !isShowBsIas.value,
      disabled: !hasBsIasAuthority.value,
      isInAllowList: isBsIasWhite.value,
    },
    {
      value: BrandSafetyTrackTypeMap.DV,
      label: i18n.t('module_ad_dv_switch_title'),
      hintKey: 'module_brand_safety_dv_info_new',
      linkUrl: 'https://tiktok.social.doubleverify.com/',
      linkKey: 'module_brand_safety_dv_info_link',
      linkPlace: 'Connect',
      hide: !isShowBsDv.value,
      disabled: !hasBsDvAuthority.value,
      isInAllowList: isBsDvWhite.value,
    },
  ]);

  /** 可使用的可见性选项 */
  const availableBrandSafetyOptions = computed(() => allViewabilityOptions.value.filter(i => !i.hide));

  const isOnlyOneBrandSafetyTrackType = computed(() => availableBrandSafetyOptions.value.length === 1);
  const isOnlyShowZefr = computed(() => isShowZefr.value && isOnlyOneBrandSafetyTrackType.value);
  const isOnlyShowBsIas = computed(() => isShowBsIas.value && isOnlyOneBrandSafetyTrackType.value);
  const isOnlyShowBsdv = computed(() => isShowBsDv.value && isOnlyOneBrandSafetyTrackType.value);

  /** 单个可选时，未授权禁用switch开关 */
  const isDisabledBrandSafetyToggle = computed(() => {
    const list = availableBrandSafetyOptions.value || [];
    return list?.length === 1 && list[0]?.disabled;
  });

  const isNotSupportedLocationForAllTrackType = computed(() => {
    if (isOnlyShowZefr.value) {
      return !isSupportedCountriesZefr.value;
    }
    if (isOnlyShowBsIas.value) {
      return !isSupportedCountriesIas.value;
    }
    if (isOnlyShowBsdv.value) {
      return !isSupportedCountriesDv.value;
    }

    return !availableBrandSafetyOptions.value.length;
  });

  const isBrandSafetyHasIas = computed(() =>
    Boolean(
      availableBrandSafetyOptions.value.find(item => item.value === BrandSafetyTrackTypeMap.IAS && !item.disabled),
    ),
  );

  /** 品牌安全监测标题 */
  const brandSafetyTrackTitle = computed(() => {
    if (isOnlyShowZefr.value) {
      return i18n.t('module_track_url_brand_safety_title_with_zefr');
    }
    if (isOnlyShowBsIas.value) {
      return i18n.t('module_track_url_brand_safety_title_with_ias');
    }
    if (isOnlyShowBsdv.value) {
      return i18n.t('module_track_url_brand_safety_title_with_dv');
    }
    return i18n.t('module_track_url_brand_safety_title');
  });

  /** 标题提示 */
  const brandSafetyTrackTitleHint = computed(() => i18n.t('module_track_url_brand_safety_title_hint'));

  return {
    isShowZefr,
    isShowBsIas,
    isShowBsDv,
    isOnlyShowZefr,
    isOnlyShowBsIas,
    isOnlyShowBsdv,
    isShowBrandSafety,
    isDisabledBrandSafetyTrack,
    isDisabledBrandSafetyToggle,
    isBrandSafetyHasIas,
    isOnlyOneBrandSafetyTrackType,
    brandSafetyTrackTitle,
    brandSafetyTrackTitleHint,
    availableBrandSafetyOptions,
    isNotSupportedLocationForAllTrackType,
    isSupportBrandSafetyTracking,
  };
}
